import { useFormContext } from 'react-hook-form';

import { Schema, SchemaNodeProps } from '@typings';
import { getNestedValue, tv } from '@utils';

import { Render } from '@components/Shared';

import { SchemaAppIntegrations } from '../SchemaAppIntegrations';

import { SchemaCollapsible } from './SchemaCollapsible';
import { SchemaNode } from './SchemaNode';

const variants = tv({
  slots: {
    container: '',
    title: '',
    description: 'text-footnote text-neural-04',
    content: 'flex flex-col gap-6',
  },
  variants: {
    heading: {
      true: {
        container: '',
        title: 'text-h4',
        description: '',
        content: 'mt-8',
      },
      false: {
        container: 'rounded-xl border border-neural-02 px-6 pb-6',
        title: 'text-h5 mt-6',
        description: 'mt-0.5',
        content: 'mt-4',
      },
    },
  },
  defaultVariants: {
    heading: false,
  },
});

type Props = SchemaNodeProps & {
  schema: Schema.Container;
  isTopLevelNode?: boolean;
};

export const SchemaContainer: React.FC<Props> = ({
  schema,
  isTopLevelNode,
  withFormatter,
  schema: { title, description, name, children, isCollapsibleField },
}) => {
  const { formState } = useFormContext();

  const slots = variants({
    heading: isTopLevelNode,
  });

  const content = (
    <SchemaAppIntegrations schema={schema}>
      <div className={slots.content()}>
        {children.map((node) => (
          <SchemaNode
            withFormatter={withFormatter}
            key={node.name}
            schema={node}
          />
        ))}
      </div>
    </SchemaAppIntegrations>
  );

  if (isCollapsibleField) {
    return (
      <SchemaCollapsible
        key={title || description}
        title={title}
        description={description}
        hasError={!!getNestedValue(formState.errors, name)}
      >
        {content}
      </SchemaCollapsible>
    );
  }

  return (
    <div key={title || description} className={slots.container()}>
      <Render if={title}>
        <p className={slots.title()}>{title}</p>
      </Render>

      <Render if={description}>
        <p className={slots.description()}>{description}</p>
      </Render>

      {content}
    </div>
  );
};
